/**
 * Utility functions and constants for the Intra admin components.
 * @module intraUtils
 */

import React from 'react';
import { IconDeviceDesktop, IconDeviceGamepad2 } from '@tabler/icons-react';
import { LanBooking, LanRegistration } from 'types/intra';
import soupImage from 'assets/soup.png';
import noSoupImage from 'assets/no-soup.png';

/**
 * Type for tracking async operation states.
 * Null when no operation is in progress, string key identifies the active operation.
 */
export type OperationKey = string | null;

export const isAssignedLocation = (location?: string | null): location is string =>
    Boolean(location) && location !== '-';

export const bookedLocations = (bookings: LanBooking[]): string[] =>
    bookings.map(booking => booking.location).filter(isAssignedLocation);

export const bookingParticipantLabel = (booking: LanBooking, registrations: LanRegistration[]): string => {
    const registration = registrations.find(
        item => item.email?.toLowerCase() === booking.email?.toLowerCase()
    );
    const name = registration ? `${registration.firstname} ${registration.lastname}` : booking.email;
    return isAssignedLocation(booking.location) ? `${name} (${booking.location})` : `${name} — ei paikkaa`;
};

export const bookingDisplayName = (booking: LanBooking, registrations: LanRegistration[]): string => {
    const registration = registrations.find(
        item => item.email?.toLowerCase() === booking.email?.toLowerCase()
    );
    return registration ? `${registration.firstname} ${registration.lastname}` : booking.email;
};

export const bookingByLocation = (bookings: LanBooking[], location: string): LanBooking | undefined =>
    bookings.find(booking => booking.location === location);

export type SeatAssignIntent =
    | { type: 'clear' }
    | { type: 'assign'; location: string }
    | { type: 'swap'; location: string; occupant: LanBooking };

export const resolveSeatAssignIntent = (
    selected: LanBooking,
    location: string,
    bookings: LanBooking[]
): SeatAssignIntent => {
    if (selected.location === location) {
        return { type: 'clear' };
    }
    const occupant = bookingByLocation(bookings, location);
    if (occupant && occupant.id !== selected.id) {
        return { type: 'swap', location, occupant };
    }
    return { type: 'assign', location };
};

/**
 * Options for registration history/archive status dropdown.
 */
export const historyOptions = [
    { key: 'active', value: 0, text: 'Aktiivinen' },
    { key: 'archived', value: 1, text: 'Arkistoitu' }
];

/**
 * Extracts a user-friendly error message from various error types.
 *
 * @param error - The error to extract message from (Error, string, or unknown)
 * @returns A string error message suitable for display
 */
const apiErrorMessages: Record<string, string> = {
    'Location already booked': 'Paikka on jo varattu.',
    'Location is already blocked': 'Paikka on jo blokattu.',
    'location blocked': 'Paikka on blokattu verkkovaraukselta.',
    'location already booked': 'Paikka on jo varattu.',
    'Location is required': 'Paikka on pakollinen.',
    'Blocked location not found': 'Blokkattua paikkaa ei löytynyt.'
};

export const toErrorMessage = (error: unknown): string => {
    if (typeof error === 'string') {
        return apiErrorMessages[error] ?? error;
    }
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
        const apiError = axiosError.response?.data?.error || axiosError.response?.data?.message;
        if (apiError) {
            return apiErrorMessages[apiError] ?? apiError;
        }
    }
    if (error instanceof Error) {
        return apiErrorMessages[error.message] ?? error.message;
    }
    return 'Tuntematon virhe tapahtui.';
};

/**
 * Converts UTC ISO string to local datetime string for datetime-local input
 * @param isoString - UTC ISO string (e.g., "2023-01-15T10:00:00.000Z")
 * @returns Local datetime string (e.g., "2023-01-15T12:00:00" if timezone is UTC+2)
 */
export const formatDateForInput = (isoString: string): string => {
    let utcString = isoString;
    if (!utcString.endsWith('Z') && !utcString.match(/[+-]\d{2}:\d{2}$/)) {
        utcString = utcString + 'Z';
    }
    const date = new Date(utcString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Converts local datetime string from datetime-local input to UTC ISO string
 * @param localDatetimeString - Local datetime string (e.g., "2023-01-15T12:00:00")
 * @returns UTC ISO string (e.g., "2023-01-15T10:00:00.000Z" if timezone is UTC+2)
 */
export const parseInputDateToISO = (localDatetimeString: string): string => {
    const date = new Date(localDatetimeString);
    return date.toISOString();
};

/**
 * Formats an ISO date string for display in Finnish locale.
 *
 * @param value - ISO date string to format, or undefined
 * @returns Formatted date string in Finnish locale, or '-' if no value
 */
export const formatDate = (value?: string): string => {
    if (!value) {
        return '-';
    }
    return new Date(value).toLocaleString('fi-FI');
};

/**
 * Formats attendance days from a registration into a comma-separated string.
 *
 * @param registration - The registration record to format attendance for
 * @returns Comma-separated day abbreviations (To, Pe, La) or '-' if none
 */
export const formatAttendance = (registration: LanRegistration): string => {
    const slots = [
        registration.attendingThu && 'To',
        registration.attendingFri && 'Pe',
        registration.attendingSat && 'La'
    ].filter(Boolean);
    return slots.length ? slots.join(', ') : '-';
};

/**
 * Formats device types from a registration into icon/text elements.
 *
 * Displays icons for PC and console, text for hanging/other device types.
 *
 * @param registration - The registration record to format devices for
 * @returns React node with device icons/text, or '-' if no devices
 */
export const formatDevices = (registration: LanRegistration): React.ReactNode => {
    const values = [
        registration.devicePc && (
            React.createElement('span', { key: 'pc', style: { marginRight: '0.5rem' }, title: 'PC' },
                React.createElement(IconDeviceDesktop, { size: 16 })
            )
        ),
        registration.deviceConsole && (
            React.createElement('span', { key: 'console', style: { marginRight: '0.5rem' }, title: 'Konsoli' },
                React.createElement(IconDeviceGamepad2, { size: 16 })
            )
        ),
        registration.deviceHanging && (
            React.createElement('span', { key: 'hanging', style: { marginRight: '0.5rem' } }, 'Hengailemassa')
        ),
        registration.deviceOther && (
            React.createElement('span', { key: 'other', style: { marginRight: '0.5rem' } },
                registration.deviceOtherComment || 'Muu'
            )
        )
    ].filter(Boolean);
    return values.length ? React.createElement(React.Fragment, null, ...values) : '-';
};

/**
 * Formats diet information from a registration into visual elements.
 *
 * Displays a soup/no-soup image based on food order status,
 * followed by diet restriction codes (L, G, V, or custom).
 *
 * @param registration - The registration record to format diet for
 * @returns React node with food image and diet codes
 */
export const formatDiet = (registration: LanRegistration): React.ReactNode => {
    const foodImage = registration.food ? soupImage : noSoupImage;
    const foodAlt = registration.food ? 'Ruoka tilattu' : 'Ei ruokailua';
    const dietOptions = [
        registration.dietL && 'L',
        registration.dietG && 'G',
        registration.dietV && 'V',
        registration.dietOther && (registration.dietOtherComment || 'Muu')
    ].filter(Boolean);

    return React.createElement('span', 
        { style: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem' } },
        React.createElement('img', { src: foodImage, alt: foodAlt, style: { width: '20px', height: '20px' } }),
        dietOptions.length > 0 && React.createElement('span', null, dietOptions.join(', '))
    );
};

/**
 * Checks if a done/completed status value is truthy.
 *
 * Handles both boolean and numeric (0/1) representations.
 *
 * @param done - The done status value (boolean, 0, 1, or undefined)
 * @returns True if the status indicates completion
 */
export const isDone = (done?: number | boolean): boolean => done === true || done === 1;

/** Display value for a registration with no booked seat. */
export const UNBOOKED_SEAT_DISPLAY = '-';

export type SortDirection = 'asc' | 'desc';

/**
 * Cycles column sort: default → asc → desc → default.
 */
export const cycleSortDirection = (current: SortDirection | null): SortDirection | null => {
    if (current === null) {
        return 'asc';
    }
    if (current === 'asc') {
        return 'desc';
    }
    return null;
};

/**
 * Normalizes a booking location string.
 *
 * Treats missing values, whitespace, and '-' (the public booking UI's
 * "freed seat" sentinel) as unbooked.
 *
 * @param location - Raw location from a booking record
 * @returns Trimmed seat id, or null when unbooked
 */
export const normalizeSeatLocation = (location?: string | null): string | null => {
    if (!location) {
        return null;
    }
    const trimmed = location.trim();
    if (!trimmed || trimmed === '-') {
        return null;
    }
    return trimmed;
};

/**
 * Builds a case-insensitive email → booked seat map from bookings.
 *
 * When an email has multiple booking rows, a row with a real location
 * is preferred over an unbooked one.
 *
 * @param bookings - Booking records that may include a location
 * @returns Map keyed by lowercased, trimmed email
 */
export const bookingLocationByEmail = (bookings: LanBooking[]): Map<string, string> => {
    const locations = new Map<string, string>();
    for (const booking of bookings) {
        const email = booking.email?.trim().toLowerCase();
        if (!email) {
            continue;
        }
        const location = normalizeSeatLocation(booking.location);
        if (!location) {
            continue;
        }
        if (!locations.has(email)) {
            locations.set(email, location);
        }
    }
    return locations;
};

/**
 * Looks up the booked seat for a registration via its email.
 *
 * @param registration - Registration whose email is used as the join key
 * @param locationsByEmail - Map produced by {@link bookingLocationByEmail}
 * @returns Seat id, or null when unbooked
 */
export const getSeatForRegistration = (
    registration: LanRegistration,
    locationsByEmail: Map<string, string>
): string | null => {
    const email = registration.email?.trim().toLowerCase();
    if (!email) {
        return null;
    }
    return locationsByEmail.get(email) ?? null;
};

/**
 * Display string for a seat cell (booked id or '-').
 */
export const displaySeatLocation = (location: string | null): string => location ?? UNBOOKED_SEAT_DISPLAY;

/**
 * Whether a seat matches a case-insensitive substring filter.
 *
 * Unbooked seats display as '-' and therefore match a filter of '-'.
 */
export const seatMatchesFilter = (location: string | null, filter: string): boolean => {
    const query = filter.trim().toLowerCase();
    if (!query) {
        return true;
    }
    return displaySeatLocation(location).toLowerCase().includes(query);
};

/**
 * Compares two seats for table sorting.
 *
 * Uses numeric localeCompare so A1, A2, A10 order correctly.
 * Unbooked seats sort last on asc and first on desc.
 */
export const compareSeats = (
    a: string | null,
    b: string | null,
    direction: SortDirection
): number => {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return direction === 'asc' ? 1 : -1;
    }
    if (!b) {
        return direction === 'asc' ? -1 : 1;
    }
    const cmp = a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    return direction === 'asc' ? cmp : -cmp;
};

const participantFilterFields = (registration: LanRegistration): string[] => {
    const firstname = registration.firstname?.trim() ?? '';
    const lastname = registration.lastname?.trim() ?? '';
    const fullName = [firstname, lastname].filter(Boolean).join(' ');
    return [
        firstname,
        lastname,
        fullName,
        registration.nickname?.trim() ?? '',
        registration.email?.trim() ?? '',
        registration.phone?.trim() ?? ''
    ].filter(Boolean);
};

/**
 * Whether a participant matches a case-insensitive substring filter.
 *
 * Searches first name, last name, full name, nickname, email, and phone.
 */
export const participantMatchesFilter = (registration: LanRegistration, filter: string): boolean => {
    const query = filter.trim().toLowerCase();
    if (!query) {
        return true;
    }
    return participantFilterFields(registration).some(field => field.toLowerCase().includes(query));
};

/**
 * Compares two participants for table sorting by last name, then first name, then nickname.
 */
export const compareParticipants = (
    a: LanRegistration,
    b: LanRegistration,
    direction: SortDirection
): number => {
    const cmp = (left?: string, right?: string) =>
        (left || '').localeCompare(right || '', 'fi', { sensitivity: 'base' });
    const last = cmp(a.lastname, b.lastname);
    if (last !== 0) {
        return direction === 'asc' ? last : -last;
    }
    const first = cmp(a.firstname, b.firstname);
    if (first !== 0) {
        return direction === 'asc' ? first : -first;
    }
    const nick = cmp(a.nickname, b.nickname);
    return direction === 'asc' ? nick : -nick;
};

